// BalanceGeneralXL - Sprint 44 Fase 3 Vista 8.
// Reporte balance general a fecha. Solo lectura. Tokens + dark.
import { useCallback, useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import * as svc from '../services/contabilidadService';
import XLInput from '../../../components/xl/XLInput';
import XLSkeleton from '../../../components/xl/XLSkeleton';
import XLBadge from '../../../components/xl/XLBadge';
import { fmt } from '../utils';

const CSS_ID = 'xl-rep-bg-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__sub { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }

.xl-rep-toolbar {
  display: flex; align-items: end; gap: 10px; flex-wrap: wrap;
}
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
  background: var(--xl-color-primary); color: var(--xl-color-text-inverse);
}
.xl-btn:hover { background: var(--xl-color-primary-hover) }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }

.xl-banner-ok {
  background: rgba(5,150,105,0.10); color: var(--xl-color-success);
  border: 1px solid rgba(5,150,105,0.28);
  padding: 10px 14px; margin-bottom: 16px;
  border-radius: var(--xl-radius-md); font-size: 13px;
}
.xl-banner-err {
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
  padding: 10px 14px; margin-bottom: 16px;
  border-radius: var(--xl-radius-md); font-size: 13px;
}

.xl-bg-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
}
@media (max-width: 720px) { .xl-bg-grid { grid-template-columns: 1fr } }
.xl-bg-col { display: flex; flex-direction: column; gap: 16px }

.xl-section {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  padding: 18px;
}
.xl-section__hd {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 10px; margin-bottom: 10px;
  border-bottom: 1px solid var(--xl-color-border);
}
.xl-section__title {
  font-size: 12px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--xl-color-text);
}
.xl-section__total {
  font-family: var(--xl-font-mono); font-weight: 600; font-size: 14px;
  color: var(--xl-color-text);
}
.xl-section__row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 4px 0; font-size: 13px;
}
.xl-section__lbl { color: var(--xl-color-text) }
.xl-section__code {
  font-family: var(--xl-font-mono); font-size: 11px;
  color: var(--xl-color-text-muted); margin-right: 8px;
}
.xl-section__val { font-family: var(--xl-font-mono); color: var(--xl-color-text) }
.xl-section__empty { font-size: 12px; color: var(--xl-color-text-muted) }

.xl-bg-totals {
  margin-top: 16px;
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
}
@media (max-width: 720px) { .xl-bg-totals { grid-template-columns: 1fr } }
.xl-bg-totals__card {
  background: rgba(37,99,235,0.08);
  border: 1px solid rgba(37,99,235,0.28);
  color: var(--xl-color-info);
  border-radius: var(--xl-radius-md);
  padding: 12px 14px;
  font-size: 13px;
  font-weight: 500;
}
[data-xl-theme="dark"] .xl-bg-totals__card {
  background: rgba(96,165,250,0.14);
  border-color: rgba(96,165,250,0.32);
}
.xl-bg-totals__val { font-family: var(--xl-font-mono); margin-left: 8px }
.xl-loading { display: flex; flex-direction: column; gap: 10px }
`;

function Seccion({ titulo, data }) {
  if (!data) return null;
  return (
    <div className="xl-section">
      <div className="xl-section__hd">
        <span className="xl-section__title">{titulo}</span>
        <span className="xl-section__total">{fmt(data.total, 0)}</span>
      </div>
      {(data.cuentas || []).length === 0 && (
        <div className="xl-section__empty">Sin saldos.</div>
      )}
      {(data.cuentas || []).map((c) => (
        <div key={c.id} className="xl-section__row">
          <span className="xl-section__lbl">
            <span className="xl-section__code">{c.codigo}</span>
            {c.nombre}
          </span>
          <span className="xl-section__val">{fmt(c.saldo, 0)}</span>
        </div>
      ))}
    </div>
  );
}

export default function BalanceGeneralXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try { setData(await svc.balanceGeneral(fecha)); }
    catch (e) {
      setErr((e && e.message) || 'Error al calcular');
      setData(null);
    } finally { setLoading(false); }
  }, [fecha]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Balance general</h2>
          <p className="xl-page__sub">A fecha de corte. Solo asientos publicados.</p>
        </div>
        <div className="xl-rep-toolbar">
          <XLInput label="Fecha" type="date" value={fecha}
            onChange={(e) => setFecha(e.target.value)} />
          <button type="button" className="xl-btn"
            onClick={load} disabled={loading}>
            {loading ? '\u2026' : 'Calcular'}
          </button>
        </div>
      </header>

      {err && <div className="xl-banner-err" role="alert">{err}</div>}

      {loading && (
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="rect" height={120} />
          <XLSkeleton variant="rect" height={220} />
        </div>
      )}

      {!loading && data && (
        <>
          {data.balanceado ? (
            <div className="xl-banner-ok" role="status">
              <XLBadge variant="success" dot>Cuadrado</XLBadge>{' '}
              Balance cuadrado: Activo = Pasivo + Patrimonio
            </div>
          ) : (
            <div className="xl-banner-err" role="alert">
              Balance NO cuadra. Revisa los asientos.
            </div>
          )}

          <div className="xl-bg-grid">
            <Seccion titulo="Activo" data={data.activo} />
            <div className="xl-bg-col">
              <Seccion titulo="Pasivo" data={data.pasivo} />
              <Seccion titulo="Patrimonio" data={data.patrimonio} />
            </div>
          </div>

          <div className="xl-bg-totals">
            <div className="xl-bg-totals__card">
              Total activo:
              <span className="xl-bg-totals__val">{fmt(data.activo && data.activo.total, 0)}</span>
            </div>
            <div className="xl-bg-totals__card">
              Total pasivo + patrimonio:
              <span className="xl-bg-totals__val">{fmt(data.total_pasivo_patrimonio, 0)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
